import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TSummaryFormInstance } from 'core/checkout/CoreSummaryForm';
import { TPaymentResult as TPaymentResultBase } from 'widgets/checkout/CheckoutMgr';
import { TAddressCheckoutFormInstance } from 'widgets/checkout/AddressCheckoutForm';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { submitForm } from 'core/toolbox/formSubmit';

type TPaymentResult = TPaymentResultBase & {
    orderNo?: string;
    token?: string;
};

/**
 * @description Checkout Summary step logic
 * @param SummaryStepMixin Base widget for extending
 * @returns CoreSummaryStepMixin class
 */
function CoreSummaryStepMixinClassCreator(SummaryStepMixin: import('widgets/checkout/SummaryStepMixin').TSummaryStepMixin) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CoreSummaryStepMixin
     * @augments SummaryStepMixin
     * @classdesc Represents summary step component with the next features:
     * 1. Manage Summary step state on the checkout
     * 2. Activate/Deactivate Summary step
     * 3. Submit Summary step
     */
    class CoreSummaryStepMixin extends SummaryStepMixin {
        expressCheckoutType: string | undefined;

        prefs() {
            return {
                summaryFormID: 'dwfrm_summary',
                paymentBillingFormID: 'paymentBillingForm',
                addToEmailListID: 'addToEmailList',
                promotionalInformationSmsID: 'promotionalInformationSms',
                promotionalInformationKakaoTalkID: 'promotionalInformationKakaoTalk',
                expressCheckoutEntryPoint: undefined as string | undefined,
                ...super.prefs()
            };
        }

        init(): void {
            super.init();

            this.clearBackFromPaymentParams();
        }

        /**
         * Removing parameter that were used to restore basket after returning from payment with back navigation
         */
        clearBackFromPaymentParams() {
            const url = new URL(window.location.href);

            if (url.searchParams.has('backFromPaymentFor')) {
                url.searchParams.delete('backFromPaymentFor');
                url.searchParams.delete('token');

                window.history.replaceState(window.history.state, '', url.href);
            }
        }

        /**
         * @description Activate summary step
         * @emits "checkout.summary.step.activated"
         */
        summaryActivate() {
            this.getByIdDeep(this.prefs().addToEmailListID, (addToEmailList: TInputCheckboxInstance) => {
                if (this.isOrderEmailSubscribed) {
                    addToEmailList.hide();
                } else {
                    addToEmailList.show();
                }
            });

            this.getByIdDeep(this.prefs().promotionalInformationSmsID, (promotionalInformationSms: TInputCheckboxInstance) => {
                if (this.isOrderSmsSubscribed) {
                    promotionalInformationSms.check();
                } else {
                    promotionalInformationSms.uncheck();
                }
            });

            this.getByIdDeep(this.prefs().promotionalInformationKakaoTalkID, (promotionalInformationKakaoTalk: TInputCheckboxInstance) => {
                if (this.isOrderKakaoSubscribed) {
                    promotionalInformationKakaoTalk.check();
                } else {
                    promotionalInformationKakaoTalk.uncheck();
                }
            });

            super.summaryActivate();
        }

        /**
         * @description Send analytics events on summary step
         */
        sendSummaryEvents() {
            setTimeout(() => {
                if (this.prefs().expressCheckoutEntryPoint) {
                    let checkoutType;

                    switch (this.prefs().expressCheckoutEntryPoint) {
                        case 'basket':
                            checkoutType = 'express_cart';
                            break;
                        case 'checkout':
                            checkoutType = 'express_shipping';
                            break;
                        case 'pdp':
                        default:
                            checkoutType = 'express_pdp';
                    }

                    this.config.checkoutType = checkoutType;

                    this.eventBus().emit('checkout.shipping.step.submitted', this);
                    this.expressCheckoutType = checkoutType;
                }

                this.eventBus().emit('checkout.billing.step.submitted', this);
            }, 0);
        }

        /**
         * @description Handle click on step edit button
         * @param el - Edit button ref element
         * @listens dom#click
         */
        editStep(el: TRefElementInstance) {
            this.expressCheckoutType = undefined;
            super.editStep(el);
        }

        /**
         * @description Submit summary form
         * @param additionalData - additional data with "Place Order" button for disabling/enabling
         * @param skipPrivacyValidation - Allows skip privacy validation if needed(e.g. PayPal)
         */
        summarySubmit(additionalData: Array<TProcessButtonInstance>, skipPrivacyValidation?: boolean) {
            if (this.prefs().processingRequest) {
                return;
            }

            const submitBtn = additionalData.find(el => {
                return el?.id === 'submitButton';
            }) as TProcessButtonInstance;

            this.ref('summaryError').hide();
            this.setPref('processingRequest', true);
            submitBtn?.startProcess();

            this.eventBus().emit('checkout.summary.step.submitted', this);

            this.submitSummaryForm(skipPrivacyValidation)
                ?.then(response => {
                    this.handleSummarySubmissionResponse(response, submitBtn);
                })
                .catch(() => {
                    this.setPref('processingRequest', false);
                    submitBtn?.stopProcess();
                });
        }

        /**
         * @description Submits the summary form to the Place Order endpoint
         * @param skipPrivacyValidation - Allows skip privacy validation if needed(e.g. PayPal)
         * @returns Place Order response, or nothing when the summary form is missing or skips the submission
         */
        submitSummaryForm(skipPrivacyValidation?: boolean): ReturnType<TSummaryFormInstance['submit']> | undefined {
            return this.getById(this.prefs().summaryFormID, (summaryForm: TSummaryFormInstance) => {
                const checkoutType = this.config.checkoutType;

                summaryForm.expressCheckoutType =
                    this.expressCheckoutType || (typeof checkoutType === 'string' && checkoutType ? checkoutType : undefined);
                summaryForm.urlPlaceOrder = this.prefs().urlPlaceOrder;
                summaryForm.setSkipPrivacyValidation(skipPrivacyValidation);

                return summaryForm.submit();
            });
        }

        /**
         * @description Handle summary submission response
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        handleSummarySubmissionResponse(response: TPaymentResult, submitBtn: TProcessButtonInstance) {
            // add type check for consistency
            if (response && response.success && response.action === 'formsubmit') {
                const { data, orderNo, token } = response;

                if (data?.actionURL && !data.actionURL.startsWith(window.location.origin)) {
                    const url = new URL(window.location.href);

                    if (orderNo) {
                        url.searchParams.set('backFromPaymentFor', orderNo);
                    }

                    if (token) {
                        url.searchParams.set('token', token);
                    }

                    window.history.replaceState(window.history.state, '', url.toString());
                }

                submitForm(data?.actionURL, data?.formData);
            } else {
                super.handleSummarySubmissionResponse(response, submitBtn);
            }
        }

        /**
         * @description Handle summary submission error
         * @param response - Server response object
         * @param submitBtn - submit button for enabling
         */
        handleSummarySubmissionError(response: TPaymentResult, submitBtn: TProcessButtonInstance) {
            this.setPref('processingRequest', false);
            submitBtn?.stopProcess();

            if (response.errorStage) {
                const stageMap = {
                    billing: { step: 'shipping', formID: this.prefs().billingFormID },
                    shipping: { step: 'shipping', formID: this.prefs().shippingFormID },
                    payment: { step: 'billing', formID: this.prefs().paymentBillingFormID }
                };
                const errorStage = stageMap[response.errorStage.stage];

                if (errorStage) {
                    this.activateStepByName(errorStage.step);

                    if (response.errorMessage) {
                        this.getById(errorStage.formID, (formComponent: TAddressCheckoutFormInstance) => {
                            formComponent.setError(response.errorMessage);
                        });
                    }
                }
            } else if (response.errorMessage) {
                super.handleSummarySubmissionError(response, submitBtn);
            }
        }
    }

    return CoreSummaryStepMixin;
}

export type TCoreSummaryStepMixin = ReturnType<typeof CoreSummaryStepMixinClassCreator>;

export type TCoreSummaryStepMixinInstance = InstanceType<TCoreSummaryStepMixin>;

export default CoreSummaryStepMixinClassCreator;
